/**
 * appendCompiled — lightweight DOM append for compiled output.
 *
 * Handles the fast path: null/boolean (skip), Node (appendChild),
 * Array (recurse), string/number (createTextNode).
 *
 * React elements (value.$$typeof) are delegated to a late-bound handler
 * registered via registerAppendReactElement(). If no handler is registered,
 * the element falls through to createTextNode (toString).
 *
 * This separation exists for tree-shaking: apps whose compiled output never
 * produces React elements can drop the entire renderCompiledComponent module
 * and its transitive dependencies.
 */
import { untrack } from './signal-core.js';
let _appendReactElement = null;
/**
 * Register the heavy-path handler for React elements (value.$$typeof).
 * Called once by the module that owns renderCompiledComponent.
 */
export function registerAppendReactElement(handler) {
    _appendReactElement = handler;
}
let _renderCompiledChild = null;
export function registerRenderCompiledChild(handler) {
    _renderCompiledChild = handler;
}
let _claimText = null;
export function registerClaimText(handler) {
    _claimText = handler;
}
let _claimReactHost = null;
export function registerClaimReactHost(handler) {
    _claimReactHost = handler;
}
let _moveGuard = null;
export function registerMoveGuard(handler) {
    _moveGuard = handler;
}
function appendText(parent, data) {
    if (_claimText) {
        const claimed = _claimText(data);
        if (claimed) {
            // Direct append onto the claimed node's own parent: it is already in
            // position - appendChild would move it past later siblings (same rule
            // as _ap$h). Fragment/other parents adopt it (effect-built children).
            if (claimed.parentNode !== parent)
                parent.appendChild(claimed);
            return;
        }
    }
    parent.appendChild(document.createTextNode(data));
}
/**
 * Append the RESULT OF `renderCompiledComponent` to a compiled parent
 * (BUG-RUNTIME-118 mechanism A).
 *
 * The compiler's "static props: call once, append result" branch used to emit a
 * RAW `parent.appendChild(result)` here. `renderCompiledComponent` hands back
 * the LIVE, connected node on a fallback-cache hit, so that raw call moved a
 * live hosted child out of the document and into the body's fresh, detached
 * tree. Two consequences, both measured on `calendar-aria`:
 *
 *   - mid-gesture the pressed node (or an ancestor) leaves the document, and
 *     Chromium drops the pending click (ATLAS-GESTURE-001);
 *   - the keep-alive comparison in `_reconcileFunctionComponent` is then asked
 *     to compare a live tree that has already been ROBBED against the tree that
 *     robbed it, so it dies at child index 0 (`<header>`: live 0 children,
 *     fresh 2) and swaps the whole body on a connected parent.
 *
 * This is deliberately NOT `appendCompiled`. That function must re-interpret an
 * arbitrary child value - signal getters, arrays, React elements, compiled-child
 * markers - and reaching a React element is what pins `render-component.ts` into
 * the graph (DANGER-ZONES "Moon floor" rule 1). This site's value comes straight
 * out of `renderCompiledComponent`, so it is ALWAYS a Node, and this helper is
 * exactly `appendCompiled`'s Node arm and nothing else: the load-bearing
 * identity early-return, the move guard, the BUG-RUNTIME-070 multi-root
 * hand-back, the append.
 *
 * The move guard is a null slot in any graph with no compat seam, so a moon
 * bundle pays one null compare (`registerMoveGuard`, `claim.ts`).
 */
export function appendCompiledChild(parent, value) {
    if (!(value instanceof Node)) {
        // Defensive only: a compiled body whose return value is not a Node (null /
        // string / number result). `appendCompiled` owns those shapes.
        appendCompiled(parent, value);
        return;
    }
    // MUST stay first: a strict DOM no-op appendChild kills a pending click 0/9
    // (ATLAS-GESTURE-001), so this early-return is load-bearing and the guard
    // below may never bypass it.
    if (value.parentNode === parent)
        return;
    if (_moveGuard !== null && _moveGuard(parent, value))
        return;
    // BUG-RUNTIME-070 multi-root hand-back - see the identical branch below.
    if (value.nodeType === 11 && value.childNodes.length === 0) {
        const persisted = value._persistedAnchorLeaves;
        if (persisted) {
            for (const leaf of persisted) {
                if (leaf && leaf.parentNode !== parent)
                    parent.appendChild(leaf);
            }
            return;
        }
    }
    parent.appendChild(value);
}
/**
 * Append a value to a DOM parent node.
 *
 * Lightweight path handles: null, undefined, boolean, Node, Array, string, number.
 * React elements ($$typeof) delegate to the registered handler or fall back to text.
 */
export function appendCompiled(parent, value) {
    // Skip nullish and booleans (same as React)
    if (value == null || typeof value === 'boolean')
        return;
    // Internal sentinel — never render (would leak "Symbol(MEMO_SKIP)" as text).
    if (typeof value === 'symbol')
        return;
    // Function child — a lazy getter emitted by the compiler for children
    // expressions that read signals (e.g. `children: () => renderContent()`).
    // Invoke inside the caller's reactive context: if the current stack frame
    // is inside a createEffect (compiled-component children-effects are), the
    // signals read inside the thunk register as deps of THAT effect, so the
    // whole children-append re-fires on change. Recurse with the result.
    if (typeof value === 'function') {
        // Compiler-branded getters (`__g(() => ...)`, createMemo/createSignal reads)
        // are OUR render thunks: a throw during their evaluation is a real render
        // error (error-boundary semantics) or a thrown promise (Suspense/lazy) and
        // MUST propagate to the enclosing catch - the EB inline stub or
        // compiledSuspense.mount. Swallowing here left boundaries reporting
        // catches:0 and lazy children permanently unresolved (BUG-COMPILER-019).
        if (value._isSignalGetter === true) {
            const result = value();
            if (result != null)
                appendCompiled(parent, result);
            return;
        }
        // Unbranded function child - user code in children position. Best-effort:
        // invoking may throw for reasons that are not render errors.
        try {
            const result = value();
            if (result != null)
                appendCompiled(parent, result);
        }
        catch (_e) { /* best-effort; caller chose to pass a function */ }
        return;
    }
    // DOM node — append directly. Claimed hydration nodes are already in
    // position under `parent`: appendChild would MOVE them to the end and
    // scramble sibling order (Card Header/Content/Footer under hydrate),
    // which then looks like duplicated / reshuffled UI. Mirror _ap$h.
    if (value instanceof Node) {
        if (value.parentNode === parent)
            return;
        // ORDERING seat. MUST stay below the identity early-return above: a strict
        // DOM no-op appendChild kills a pending click 0/9 (ATLAS-GESTURE-001), so
        // that guard is load-bearing and nothing here may bypass it.
        if (_moveGuard !== null && _moveGuard(parent, value))
            return;
        // BUG-RUNTIME-070: an EMPTY DocumentFragment carrying `_persistedAnchorLeaves`
        // is the fallback reconciler's hand-back for a MULTI-ROOT compat child that is
        // already live in the previous render's DOM (Base UI Checkbox/Switch/Select =
        // visible element + hidden input). Appending it moves nothing, so the child's
        // real DOM stays stranded in the detached previous tree and the component
        // silently vanishes. The single-root hand-back is the live element itself and
        // appendChild MOVES it — do the same for the multi-root case. Only the compat
        // reconcile-dispatch path leaves these leaves in place (it reconciles them
        // where they stand); a compiled body is building a fresh tree and must take
        // them with it.
        if (value.nodeType === 11 && value.childNodes.length === 0) {
            const persisted = value._persistedAnchorLeaves;
            if (persisted) {
                for (const leaf of persisted) {
                    if (leaf && leaf.parentNode !== parent)
                        parent.appendChild(leaf);
                }
                return;
            }
        }
        parent.appendChild(value);
        return;
    }
    // Array — recurse over each item
    if (Array.isArray(value)) {
        for (const item of value)
            appendCompiled(parent, item);
        return;
    }
    // COV-005 compiled-child marker — render to a node via renderCompiledComponent and append.
    // Only reached on the COMPILED-parent path (compat parents convert markers to jsx upstream).
    if (value && typeof value === 'object' && value.$$jetChild === true && _renderCompiledChild) {
        const node = _renderCompiledChild(value);
        if (node != null)
            appendCompiled(parent, node);
        return;
    }
    // React element — delegate to heavy path if registered.
    // Wrapped in untrack so signal reads that happen during descendant
    // component rendering (e.g. Gap 2's `typeof X === 'function' ? X() : X`
    // unwrap for custom-hook arguments) do NOT subscribe an ancestor's
    // children-effect to those signals. Descendant components set their
    // own currentListener during inner createEffect bodies, so fine-grained
    // reactivity still works — only the ambient ancestor subscription is blocked.
    if (value && typeof value === 'object' && value.$$typeof) {
        if (_appendReactElement) {
            // Hydration: consume the server's rendering of this HOST element
            // first (see registerClaimReactHost above). Component-typed elements
            // keep today's path - their eventual host output mounts through the
            // fallback reconciler, not this branch.
            if (_claimReactHost && typeof value.type === 'string') {
                const consumed = _claimReactHost(parent, value.type);
                if (consumed) {
                    const lastBefore = parent.lastChild;
                    untrack(() => _appendReactElement(parent, value));
                    if (consumed.anchor) {
                        // Move the freshly appended tail into the consumed node's slot.
                        let n = lastBefore ? lastBefore.nextSibling : parent.firstChild;
                        while (n && n !== consumed.anchor) {
                            const next = n.nextSibling;
                            parent.insertBefore(n, consumed.anchor);
                            n = next;
                        }
                    }
                    return;
                }
            }
            untrack(() => _appendReactElement(parent, value));
            return;
        }
        // No handler registered — fall through to text node
    }
    // String, number, or an unhandled object — create a text node.
    // The object case is typically a keyedList row proxy wrapping a primitive.
    // Coerce explicitly via Symbol.toPrimitive: a nullish payload (e.g. a row
    // wrapping `null`) renders nothing, matching React's `{null}` → no output,
    // instead of producing the literal text "null" or throwing on coercion.
    //
    // BUG-EXPORT-024 / BUG-COMPILER-003…019: a plain object (leftover jsx()
    // element when the lean export runtime has no `_appendReactElement`, or any
    // other element-shaped leak) used to silently become the text
    // "[object Object]". Mirror `toDisplayString` / the injected
    // `__toDisplayString` tripwire — fail loud so suites catch it.
    if (value && typeof value === 'object') {
        const toPrim = value[Symbol.toPrimitive];
        if (typeof toPrim === 'function') {
            const prim = toPrim.call(value, 'string');
            if (prim == null)
                return;
            appendText(parent, String(prim));
            return;
        }
        const kind = value.$$typeof
            ? 'React element'
            : Object.prototype.toString.call(value).slice(8, -1) || 'object';
        throw new Error(`[jetshake] ${kind} hit the DOM-child text fallback (appendCompiled). ` +
            `Element-shaped values must be Nodes, thunks, or handled React elements — ` +
            `compiler/export bug (see BUG-EXPORT-024 / BUG-COMPILER-019).`);
    }
    appendText(parent, String(value));
}
